import { useState, useEffect } from "react"
import { Col, Form, Row } from "react-bootstrap"
import "react-datepicker/dist/react-datepicker.css"
import { useSelector, useDispatch } from "react-redux"
import WarningModal from "../../../components/WarningModal"
import Typeahead from "../../../components/Typeahead"
import { getStates } from "../../../data/enumSlice"
import { getCurrentUser } from "../../../data/authenticationSlice"
import { getAgencyList, getContactsByAgency } from "../../../data/agencySlice"
import { poBoxRegex } from "../../../constant/utils"

const ApplicantInformation = ({ applicationData, updateApplication, validationError, type }) => {
    const dispatch = useDispatch()

    const { states } = useSelector((state) => state.enums)
    const { user } = useSelector((state) => state.authentication)
    const { agencyList, contacts, fetchStatus: agencyFetchStatus } = useSelector((state) => state.agency)

    const [showWarning, setShowWarning] = useState(false)
    const [modalDisplayed, setModalDisplayed] = useState(false)
    const [errors, setErrors] = useState({})
    const [selectedAgencyId, setSelectedAgencyId] = useState("")
    const [selectedContactId, setSelectedContactId] = useState("")

    useEffect(() => {
        !states?.length && dispatch(getStates())
        !Object.keys(user)?.length && dispatch(getCurrentUser())
        type === "adminCreateApplication" && !agencyList?.length && dispatch(getAgencyList())
    }, [type])

    // Load contacts when editing an application that already has a brokerage selected
    // Only run on initial load, not when user manually selects an agency
    useEffect(() => {
        if (type === "adminCreateApplication" && applicationData?.brokerageId && agencyList?.length && !selectedAgencyId) {
            dispatch(getContactsByAgency(applicationData.brokerageId))
            setSelectedAgencyId(applicationData.brokerageId)
        }
    }, [type, applicationData?.brokerageId, agencyList?.length])

    const handleShowWarning = () => {
        if (!modalDisplayed) {
            setShowWarning(true)
            setModalDisplayed(true)
        }
    }

    const handleCloseWarning = () => {
        setShowWarning(false)
    }

    const handleFocus = (e) => {
        if (!modalDisplayed) {
            e.target.blur()
            handleShowWarning()
        }
    }

    const handleBlur = (event) => {
        const { name, value } = event?.target || {}
        if (name === "firstNamedInsuredCompany" && value) {
            if (value === `${user?.firstName} ${user?.lastName}`) {
                setErrors((preErrors) => ({
                    ...preErrors,
                    [name]: "The insured name cannot be the same as the agent name."
                }))
            } else {
                setErrors((preErrors) => ({
                    ...preErrors,
                    [name]: ""
                }))
            }
        } else if (name === "firstNamedInsuredAddress" && value) {
            if (poBoxRegex.test(value?.trim())) {
                setErrors((preErrors) => ({
                    ...preErrors,
                    [name]: "P.O. Box addresses are not allowed."
                }))
            } else {
                setErrors((preErrors) => ({
                    ...preErrors,
                    [name]: ""
                }))
            }
        }
    }

    const handleAgencyChange = (agencyId) => {
        setSelectedAgencyId(agencyId)
        setSelectedContactId("")
        if (agencyId) {
            dispatch(getContactsByAgency(agencyId))
        }
        updateApplication({
            currentTarget: {
                name: "brokerageId",
                value: agencyId
            }
        })
    }

    const handleContactChange = (contactId) => {
        setSelectedContactId(contactId)
        updateApplication({
            currentTarget: {
                name: "brokerContactId",
                value: contactId
            }
        })
    }

    return (
        <Form>
            <WarningModal
                show={showWarning}
                handleClose={handleCloseWarning}
                message={"Only one First Named Insured is allowed. You may add others further down in the application."}
            />
            {type === "adminCreateApplication" && (
                <>
                    <Row className="mb-2">
                        <Form.Group
                            as={Col}
                            controlId="brokerageId">
                            <Form.Label>
                                Broker Agency <span className="text-danger">*</span>
                            </Form.Label>
                            <Typeahead
                                options={agencyList || []}
                                value={selectedAgencyId || applicationData?.brokerageId || ""}
                                onChange={(agencyId) => handleAgencyChange(agencyId)}
                                getOptionLabel={(agency) => agency?.name ?? agency?.Name ?? agency?.agencyName ?? ""}
                                placeholder="Search and select broker agency..."
                                isInvalid={!!validationError?.brokerageId}
                                size="sm"
                            />
                        </Form.Group>
                    </Row>
                    <Row className="mb-2">
                        <Form.Group
                            as={Col}
                            controlId="brokerContactId">
                            <Form.Label>
                                Broker Contact <span className="text-danger">*</span>
                            </Form.Label>
                            <Typeahead
                                options={contacts || []}
                                value={selectedContactId || applicationData?.brokerContactId || ""}
                                onChange={(contactId) => handleContactChange(contactId)}
                                getOptionLabel={(contact) => [contact?.firstName, contact?.lastName].filter(Boolean).join(" ") || ""}
                                placeholder={agencyFetchStatus === "loading" ? "Loading contacts..." : "Search and select broker contact..."}
                                disabled={(!selectedAgencyId && !applicationData?.brokerageId) || agencyFetchStatus === "loading"}
                                isInvalid={!!validationError?.brokerContactId}
                                size="sm"
                            />
                        </Form.Group>
                    </Row>
                </>
            )}
            <Row className="mb-2">
                <Form.Group
                    as={Col}
                    controlId="firstNamedInsuredCompany">
                    <Form.Label>Proposed First Named Insured</Form.Label>
                    <Form.Control
                        type="text"
                        className={`form-control-sm ${
                            errors?.firstNamedInsuredCompany || validationError.firstNamedInsuredCompany ? "error" : ""
                        }`}
                        placeholder="Enter name of applicant"
                        name="firstNamedInsuredCompany"
                        onChange={updateApplication}
                        value={applicationData?.firstNamedInsuredCompany || ""}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                    />
                </Form.Group>
                {errors?.firstNamedInsuredCompany && (
                    <div className="text-error">{errors?.firstNamedInsuredCompany}</div>
                )}
            </Row>
            <Row className="mb-2">
                <Form.Group
                    as={Col}
                    controlId="firstNamedInsuredContact">
                    <Form.Label>Primary Contact at First Named Insured</Form.Label>
                    <Form.Control
                        type="text"
                        className={`form-control-sm ${validationError.firstNamedInsuredContact ? "error" : ""}`}
                        placeholder="Enter principal contact"
                        name="firstNamedInsuredContact"
                        onChange={updateApplication}
                        value={applicationData?.firstNamedInsuredContact || ""}
                    />
                </Form.Group>
            </Row>
            <Row className="mb-2">
                <Form.Group
                    as={Col}
                    controlId="firstNamedInsuredAddress">
                    <Form.Label>Mailing address of First Named Insured</Form.Label>
                    <Form.Control
                        type="text"
                        className={`form-control-sm ${
                            errors?.firstNamedInsuredAddress || validationError?.firstNamedInsuredAddress ? "error" : ""
                        }`}
                        placeholder="Enter mailing address"
                        name="firstNamedInsuredAddress"
                        onChange={updateApplication}
                        value={applicationData?.firstNamedInsuredAddress || ""}
                        onBlur={handleBlur}
                    />
                </Form.Group>
                {errors?.firstNamedInsuredAddress && (
                    <div className="text-error">{errors?.firstNamedInsuredAddress}</div>
                )}
                <Form.Group
                    as={Col}
                    controlId="firstNamedInsuredCity">
                    <Form.Label>City</Form.Label>
                    <Form.Control
                        type="text"
                        className={`form-control-sm ${validationError?.firstNamedInsuredCity ? "error" : ""}`}
                        placeholder="Enter city"
                        name="firstNamedInsuredCity"
                        onChange={updateApplication}
                        value={applicationData?.firstNamedInsuredCity || ""}
                    />
                </Form.Group>
            </Row>
            <Row className="mb-2">
                <Form.Group
                    as={Col}
                    controlId="firstNamedInsuredState">
                    <Form.Label>State</Form.Label>
                    <Form.Select
                        name="firstNamedInsuredState"
                        className={`form-select-sm ${validationError?.firstNamedInsuredState ? "error" : ""}`}
                        onChange={updateApplication}
                        value={applicationData?.firstNamedInsuredState || ""}>
                        <option value={""}>--- Select ---</option>
                        {states?.map((state) => (
                            <option
                                key={state?.value}
                                value={state?.value}>
                                {state?.name}
                            </option>
                        ))}
                    </Form.Select>
                </Form.Group>
                <Form.Group
                    as={Col}
                    name="zip"
                    controlId="firstNamedInsuredZipCode">
                    <Form.Label>Zip</Form.Label>
                    <Form.Control
                        type="text"
                        className={`form-control-sm ${validationError?.firstNamedInsuredZipCode ? "error" : ""}`}
                        placeholder="Enter zip code"
                        name="firstNamedInsuredZipCode"
                        onChange={updateApplication}
                        value={applicationData?.firstNamedInsuredZipCode || ""}
                    />
                </Form.Group>
            </Row>
        </Form>
    )
}

export default ApplicantInformation
